import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session, type AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { ensureActiveOrgId } from './org';
import { ensureAdminEmployeeRecord } from '../modules/payroll/services/adminEmployeeProvisioningService';
import { setSentryUser } from './sentry';

// Organization type for multi-business support
export interface Organization {
  id: string;
  name: string;
  owner_id: string | null;
  role: string;
  is_owner: boolean;
  is_archived: boolean;
  member_count: number;
  created_at?: string;
}

type OrgMembershipRow = {
  org_id: string | null;
  role: string | null;
};

type OrganizationRecord = {
  id: string;
  name: string;
  owner_id: string | null;
  is_archived?: boolean | null;
  created_at?: string;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  tenantId: string | null;  // Alias for currentOrg.id (backward compat)

  // Multi-org support
  organizations: Organization[];
  currentOrg: Organization | null;
  orgsLoading: boolean;

  // Auth functions
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any; needsEmailVerification: boolean }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;

  // Multi-org functions
  switchOrganization: (orgId: string) => Promise<void>;
  createOrganization: (name: string) => Promise<Organization | null>;
  archiveOrganization: (orgId: string) => Promise<boolean>;
  loadOrganizations: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const LOGOUT_MARKER_KEY = 'bizsherpa_logout_in_progress_at';
const LOGOUT_MARKER_TTL_MS = 15000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState<string | null>(null);

  // Multi-org state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const metadataSyncRef = useRef<{ userId: string | null; orgId: string | null; inflight: boolean }>({
    userId: null,
    orgId: null,
    inflight: false,
  });

  const extractTenantId = (session: Session | null): string | null => {
    if (!session?.user) return null;
    const metadata = session.user.app_metadata || session.user.user_metadata || {};
    const metaRecord = metadata as Record<string, unknown>;
    return (metaRecord.tenant_id as string) || (metaRecord.org_id as string) || null;
  };

  const setLogoutMarker = () => {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(LOGOUT_MARKER_KEY, String(Date.now()));
  };

  const clearLogoutMarker = () => {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(LOGOUT_MARKER_KEY);
  };

  const hasRecentLogoutMarker = () => {
    if (typeof window === 'undefined') return false;
    const raw = sessionStorage.getItem(LOGOUT_MARKER_KEY);
    if (!raw) return false;

    const timestamp = Number(raw);
    if (!Number.isFinite(timestamp)) {
      sessionStorage.removeItem(LOGOUT_MARKER_KEY);
      return false;
    }

    const isFresh = Date.now() - timestamp < LOGOUT_MARKER_TTL_MS;
    if (!isFresh) {
      sessionStorage.removeItem(LOGOUT_MARKER_KEY);
    }

    return isFresh;
  };

  const reconcileActiveOrganization = useCallback(async (nextUser: User): Promise<string | null> => {
    const resolvedOrgId = await ensureActiveOrgId(supabase, nextUser.id);

    setTenantId(resolvedOrgId);
    if (typeof window !== 'undefined') {
      if (resolvedOrgId) {
        localStorage.setItem('current_org_id', resolvedOrgId);
        localStorage.setItem('active_org_id', resolvedOrgId);
      } else {
        localStorage.removeItem('current_org_id');
        localStorage.removeItem('active_org_id');
      }
    }

    const metadata = (nextUser.app_metadata || {}) as Record<string, unknown>;
    const metadataTenantId = (metadata.tenant_id as string) || (metadata.org_id as string) || null;

    if (
      resolvedOrgId &&
      metadataTenantId !== resolvedOrgId &&
      !(
        metadataSyncRef.current.inflight &&
        metadataSyncRef.current.userId === nextUser.id &&
        metadataSyncRef.current.orgId === resolvedOrgId
      ) &&
      !(
        metadataSyncRef.current.userId === nextUser.id &&
        metadataSyncRef.current.orgId === resolvedOrgId
      )
    ) {
      metadataSyncRef.current = {
        userId: nextUser.id,
        orgId: resolvedOrgId,
        inflight: true,
      };

      try {
        await supabase.auth.updateUser({ data: { tenant_id: resolvedOrgId, org_id: resolvedOrgId } });
      } catch (error) {
        metadataSyncRef.current = {
          userId: null,
          orgId: null,
          inflight: false,
        };
        throw error;
      }

      metadataSyncRef.current = {
        userId: nextUser.id,
        orgId: resolvedOrgId,
        inflight: false,
      };
    }

    return resolvedOrgId;
  }, []);

  // Ensure an org exists in the `orgs` table (which org_members FK references)
  // and that the user has an org_members row for it.
  const ensureOrgMembership = async (orgId: string, orgName: string, userId: string) => {
    // org_members.org_id FK references `orgs` table, so ensure a row exists there
    await supabase
      .from('orgs')
      .upsert({ id: orgId, org_name: orgName }, { onConflict: 'id' });

    // Now insert/upsert the org_members row
    await supabase
      .from('org_members')
      .upsert({
        org_id: orgId,
        user_id: userId,
        role: 'ADMIN',
        status: 'active',
      }, { onConflict: 'org_id,user_id' });
  };

  // Auto-create a default organization for a user who has none.
  // First checks if the user already owns an org (to prevent duplicates),
  // then creates one and ensures org_members is always populated.
  const autoCreateDefaultOrg = async (userId: string): Promise<Organization | null> => {
    // Guard: check if this user already owns an organization
    const { data: existingOrgs } = await supabase
      .from('organizations')
      .select('id, name, owner_id, created_at')
      .eq('owner_id', userId)
      .eq('is_active', true)
      .eq('is_archived', false)
      .limit(1);

    if (existingOrgs && existingOrgs.length > 0) {
      const existingOrg = existingOrgs[0];
      // Ensure orgs + org_members rows exist
      await ensureOrgMembership(existingOrg.id, existingOrg.name, userId);

      return {
        id: existingOrg.id,
        name: existingOrg.name,
        owner_id: existingOrg.owner_id,
        role: 'ADMIN',
        is_owner: true,
        is_archived: false,
        member_count: 1,
        created_at: existingOrg.created_at,
      };
    }

    // No existing org — create one via direct insert
    const { data: newOrgData, error: createError } = await supabase
      .from('organizations')
      .insert({
        name: 'My Business',
        owner_id: userId,
        is_active: true,
        is_archived: false,
      })
      .select()
      .single();

    if (createError || !newOrgData) {
      console.error('[Auth] Failed to create organization:', createError);
      return null;
    }

    // Ensure orgs + org_members rows exist
    await ensureOrgMembership(newOrgData.id, newOrgData.name, userId);

    return {
      id: newOrgData.id,
      name: newOrgData.name,
      owner_id: newOrgData.owner_id,
      role: 'ADMIN',
      is_owner: true,
      is_archived: false,
      member_count: 1,
      created_at: newOrgData.created_at,
    };
  };

  const resolveOrganizationsFromMemberships = useCallback(async (userId: string): Promise<Organization[]> => {
    const [memberRes, ownerRes] = await Promise.all([
      supabase
        .from('org_members')
        .select('org_id, role')
        .eq('user_id', userId),
      supabase
        .from('organizations')
        .select('id, name, owner_id, is_archived, created_at')
        .eq('owner_id', userId)
        .eq('is_active', true)
        .eq('is_archived', false),
    ]);

    const memberRows = ((memberRes.data ?? []) as OrgMembershipRow[])
      .filter((row) => Boolean(row.org_id));

    let memberOrganizations: OrganizationRecord[] = [];
    if (memberRows.length > 0) {
      const memberOrgIds = [...new Set(memberRows.map((row) => row.org_id).filter((orgId): orgId is string => Boolean(orgId)))];
      const { data: orgRows, error: orgError } = await supabase
        .from('organizations')
        .select('id, name, owner_id, is_archived, created_at')
        .in('id', memberOrgIds)
        .eq('is_active', true)
        .eq('is_archived', false);

      if (orgError) {
        console.warn('[Auth] Failed to load organization records for memberships:', orgError.message);
      } else {
        memberOrganizations = (orgRows ?? []) as OrganizationRecord[];
      }
    }

    const memberById = new Map(memberRows.map((row) => [row.org_id, row.role ?? 'MEMBER']));

    const memberOrgs: Organization[] = memberOrganizations.map((org) => ({
      id: org.id,
      name: org.name,
      owner_id: org.owner_id,
      role: memberById.get(org.id) ?? 'MEMBER',
      is_owner: org.owner_id === userId,
      is_archived: org.is_archived || false,
      member_count: 1,
      created_at: org.created_at,
    }));

    const ownedOrgs: Organization[] = ((ownerRes.data ?? []) as OrganizationRecord[]).map((org) => ({
      id: org.id,
      name: org.name,
      owner_id: org.owner_id,
      role: 'ADMIN',
      is_owner: org.owner_id === userId,
      is_archived: org.is_archived || false,
      member_count: 1,
      created_at: org.created_at,
    }));

    return [...memberOrgs, ...ownedOrgs].filter(
      (org, index, list) => list.findIndex((candidate) => candidate.id === org.id) === index,
    );
  }, []);

  const filterPlaceholderOrganizations = useCallback((orgs: Organization[]): Organization[] => {
    const normalized = orgs.filter(Boolean);
    const hasRealOrganizations = normalized.some(
      (org) => org.name.trim().toLowerCase() !== 'my business',
    );

    return hasRealOrganizations
      ? normalized.filter((org) => org.name.trim().toLowerCase() !== 'my business')
      : normalized;
  }, []);

  const loadOrganizationsForUser = useCallback(async (
    activeUser: User,
    preferredOrgId?: string | null,
  ): Promise<Organization | null> => {
    const [profileRes, directOrgs] = await Promise.all([
      supabase
        .from('profiles')
        .select('default_organization_id, invited_via_org_id')
        .eq('id', activeUser.id)
        .maybeSingle(),
      resolveOrganizationsFromMemberships(activeUser.id),
    ]);

    let orgs = filterPlaceholderOrganizations(directOrgs);
    const profile = profileRes.data;
    const savedOrgId = typeof window !== 'undefined'
      ? (localStorage.getItem('current_org_id') || localStorage.getItem('active_org_id'))
      : null;
    const candidateOrgIds = [
      preferredOrgId,
      profile?.default_organization_id,
      profile?.invited_via_org_id,
      savedOrgId,
    ].filter((orgId): orgId is string => Boolean(orgId));

    setOrganizations(orgs);

    let matchingOrg =
      candidateOrgIds
        .map((orgId) => orgs.find((org) => org.id === orgId) ?? null)
        .find(Boolean) ?? orgs[0] ?? null;

    setCurrentOrg(matchingOrg);

    if (matchingOrg) {
      setTenantId(matchingOrg.id);
      if (typeof window !== 'undefined') {
        localStorage.setItem('current_org_id', matchingOrg.id);
        localStorage.setItem('active_org_id', matchingOrg.id);
      }
    } else {
      setTenantId(null);
      if (typeof window !== 'undefined') {
        localStorage.removeItem('current_org_id');
        localStorage.removeItem('active_org_id');
      }
    }

    return matchingOrg;
  }, [filterPlaceholderOrganizations, resolveOrganizationsFromMemberships]);

  // Load all organizations for the current user
  const loadOrganizations = useCallback(async () => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrg(null);
      return;
    }

    setOrgsLoading(true);
    try {
      await loadOrganizationsForUser(user);
    } catch (err) {
      console.error('Error loading organizations:', err);
      setOrganizations([]);
      setCurrentOrg(null);
      setTenantId(null);
    } finally {
      setOrgsLoading(false);
    }
  }, [loadOrganizationsForUser, user]);

  // Switch to a different organization
  const switchOrganization = useCallback(async (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (!org) {
      console.error('Organization not found:', orgId);
      return;
    }

    setCurrentOrg(org);
    setTenantId(orgId);

    // Persist to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('current_org_id', orgId);
      localStorage.setItem('active_org_id', orgId);
    }

    // Update user metadata
    await supabase.auth.updateUser({ data: { tenant_id: orgId, org_id: orgId } });

    if (user) {
      await supabase
        .from('profiles')
        .update({ default_organization_id: orgId })
        .eq('id', user.id);
    }

    if (typeof window !== 'undefined') {
      window.location.href = '/dashboard';
    }
  }, [organizations, user]);

  // Create a new organization
  const createOrganization = useCallback(async (name: string): Promise<Organization | null> => {
    if (!user) return null;

    try {
      // Check if user can create more businesses (plan limit check)
      const { data: canCreate, error: checkError } = await supabase
        .rpc('can_create_business');

      if (checkError) {
        console.warn('Could not check business limit, proceeding anyway:', checkError);
      } else if (!canCreate) {
        throw new Error('You have reached your business limit. Please upgrade your plan to add more businesses.');
      }

      // Create the organization
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: name.trim(),
          owner_id: user.id,
          is_active: true,
          is_archived: false,
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Create org_members entry for owner
      const { error: memberError } = await supabase
        .from('org_members')
        .insert({
          org_id: orgData.id,
          user_id: user.id,
          role: 'ADMIN',
          status: 'active',
        });

      if (memberError) {
        console.error('Error creating org membership:', memberError);
      }

      const newOrg: Organization = {
        id: orgData.id,
        name: orgData.name,
        owner_id: orgData.owner_id,
        role: 'ADMIN',
        is_owner: true,
        is_archived: false,
        member_count: 1,
        created_at: orgData.created_at,
      };

      // Update state
      setOrganizations(prev => [...prev, newOrg]);

      return newOrg;
    } catch (err) {
      console.error('Error creating organization:', err);
      throw err;
    }
  }, [user]);

  // Archive an organization
  const archiveOrganization = useCallback(async (orgId: string): Promise<boolean> => {
    if (!user) return false;

    const org = organizations.find(o => o.id === orgId);
    if (!org || !org.is_owner) {
      console.error('Cannot archive: not the owner or org not found');
      return false;
    }

    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          is_archived: true,
          archived_at: new Date().toISOString(),
        })
        .eq('id', orgId)
        .eq('owner_id', user.id);

      if (error) throw error;

      // Update state
      setOrganizations(prev => prev.filter(o => o.id !== orgId));

      // If we archived the current org, switch to another
      if (currentOrg?.id === orgId) {
        const remaining = organizations.filter(o => o.id !== orgId);
        if (remaining.length > 0) {
          await switchOrganization(remaining[0].id);
        } else {
          setCurrentOrg(null);
          setTenantId(null);
        }
      }

      return true;
    } catch (err) {
      console.error('Error archiving organization:', err);
      return false;
    }
  }, [user, organizations, currentOrg, switchOrganization]);

  useEffect(() => {
    let isMounted = true;
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 10000);

    const syncTenantId = (orgId: string | null) => {
      if (!isMounted) return;

      setTenantId(orgId);

      if (typeof window === 'undefined') return;

      if (orgId) {
        localStorage.setItem('current_org_id', orgId);
        localStorage.setItem('active_org_id', orgId);
      } else {
        localStorage.removeItem('current_org_id');
        localStorage.removeItem('active_org_id');
      }
    };

    const applySession = async (
      nextSession: Session | null,
      authEvent?: AuthChangeEvent | 'INITIAL_SESSION',
    ) => {
      if (!isMounted) return;

      if (authEvent === 'SIGNED_IN') {
        clearLogoutMarker();
      }

      if (nextSession?.user && hasRecentLogoutMarker() && authEvent !== 'SIGNED_IN') {
        clearClientAuthState();
        return;
      }

      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setSentryUser(
        nextSession?.user
          ? { id: nextSession.user.id, email: nextSession.user.email ?? null }
          : null,
      );

      if (!nextSession?.user) {
        metadataSyncRef.current = { userId: null, orgId: null, inflight: false };
        syncTenantId(null);
        return;
      }

      const storedOrgId = typeof window !== 'undefined'
        ? (localStorage.getItem('current_org_id') || localStorage.getItem('active_org_id'))
        : null;

      const metadataTenantId = extractTenantId(nextSession);
      if (metadataTenantId) {
        syncTenantId(metadataTenantId);
      } else if (storedOrgId) {
        syncTenantId(storedOrgId);
      }

      let resolvedOrgId = metadataTenantId || storedOrgId;
      try {
        resolvedOrgId = await reconcileActiveOrganization(nextSession.user);
      } catch {
        if (!storedOrgId) {
          syncTenantId(null);
        }
      }

      setOrgsLoading(true);
      try {
        await loadOrganizationsForUser(nextSession.user, resolvedOrgId);
      } catch (error) {
        console.error('[Auth] Failed to hydrate organizations from session:', error);
      } finally {
        setOrgsLoading(false);
      }
    };

    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        clearTimeout(timeoutId);
        await applySession(session, 'INITIAL_SESSION');
        if (isMounted) {
          setLoading(false);
        }
      })
      .catch(() => {
        clearTimeout(timeoutId);
        if (isMounted) {
          setLoading(false);
        }
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      void applySession(session, event);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [reconcileActiveOrganization]);

  // Load organizations when user changes
  useEffect(() => {
    if (user && !loading && !orgsLoading && organizations.length === 0) {
      void loadOrganizations();
    }
  }, [user, loading, loadOrganizations, orgsLoading, organizations.length]);

  // Sync tenantId with currentOrg when organizations are loaded
  // Only runs when currentOrg.id actually changes (not on tenantId changes)
  useEffect(() => {
    if (currentOrg && tenantId !== currentOrg.id) {
      setTenantId(currentOrg.id);
      if (typeof window !== 'undefined') {
        localStorage.setItem('current_org_id', currentOrg.id);
        localStorage.setItem('active_org_id', currentOrg.id);
      }
    }
  }, [currentOrg?.id]);

  useEffect(() => {
    if (!user || !currentOrg || !tenantId) return;

    let cancelled = false;
    const runProvisioning = () => {
      if (cancelled) return;

      void ensureAdminEmployeeRecord(user, currentOrg, tenantId).catch((error) => {
        console.error('Failed to auto-provision admin employee record:', error);
      });
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleHandle = window.requestIdleCallback(runProvisioning, { timeout: 2000 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleHandle);
      };
    }

    const timeoutHandle = window.setTimeout(runProvisioning, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutHandle);
    };
  }, [currentOrg, tenantId, user]);

  const signIn = async (email: string, password: string) => {
    clearLogoutMarker();
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (!error && data.session) {
      clearLogoutMarker();
      setSession(data.session);
      setUser(data.session.user);
      void reconcileActiveOrganization(data.session.user).catch((reconcileError) => {
        console.error('[Auth] Failed to reconcile organization after sign-in:', reconcileError);
      });
    }
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    clearLogoutMarker();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (!error && data.session) {
      clearLogoutMarker();
      setSession(data.session);
      setUser(data.session.user);
      setSentryUser({ id: data.session.user.id, email: data.session.user.email ?? null });
      void reconcileActiveOrganization(data.session.user).catch((reconcileError) => {
        console.error('[Auth] Failed to reconcile organization after sign-up:', reconcileError);
      });
    }
    return { error, needsEmailVerification: Boolean(data.user && !data.session) };
  };

  const clearClientAuthState = () => {
    metadataSyncRef.current = { userId: null, orgId: null, inflight: false };
    setSentryUser(null);
    setUser(null);
    setSession(null);
    setTenantId(null);
    setOrganizations([]);
    setCurrentOrg(null);
    setOrgsLoading(false);
    setLoading(false);

    if (typeof window !== 'undefined') {
      localStorage.removeItem('current_org_id');
      localStorage.removeItem('active_org_id');
      localStorage.removeItem('current_project_id');

      Object.keys(localStorage).forEach((key) => {
        if (key.includes('supabase.auth.token') || /^sb-.*-auth-token$/.test(key)) {
          localStorage.removeItem(key);
        }
      });

      Object.keys(sessionStorage).forEach((key) => {
        if (key.includes('supabase.auth.token') || /^sb-.*-auth-token$/.test(key)) {
          sessionStorage.removeItem(key);
        }
      });
    }
  };

  const signOut = async () => {
    let signOutError: unknown = null;
    setLogoutMarker();

    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        signOutError = error;
      }
    } catch (error) {
      signOutError = error;
    } finally {
      clearClientAuthState();
    }

    if (signOutError) {
      console.error('[Auth] Sign-out fell back to local session clear:', signOutError);
    }

    if (typeof window !== 'undefined') {
      window.location.replace('/login?logged_out=1');
    }
  };

  const refreshSession = async () => {
    const { data: { session } } = await supabase.auth.refreshSession();
    setSession(session);
    setUser(session?.user ?? null);
    setTenantId(extractTenantId(session));
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      tenantId,
      // Multi-org support
      organizations,
      currentOrg,
      orgsLoading,
      // Auth functions
      signIn,
      signUp,
      signOut,
      refreshSession,
      // Multi-org functions
      switchOrganization,
      createOrganization,
      archiveOrganization,
      loadOrganizations,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
